import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { coverSrc, copyText } from "../lib/api";

const COVER_TONES = ["#2E3A2F", "#C84B31", "#1F2A44", "#6B4E31", "#3D2B3F", "#121210"];

export function BookCover({ src, title, author, className = "", testid }) {
  const tone = COVER_TONES[(title || "").length % COVER_TONES.length];
  return (
    <div className={`${className.includes("absolute") ? "" : "relative"} aspect-[2/3] overflow-hidden book-shadow [container-type:inline-size] bg-[#EFEAE0] ${className}`} data-testid={testid}>
      {src ? (
        <img src={coverSrc(src)} alt={title} className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 flex flex-col justify-between p-[9%] text-[#FDFBF7]" style={{ background: tone }}>
          <span className="font-type text-[0.55rem] uppercase tracking-[0.25em] opacity-70">AuthorOS Press</span>
          <span className="font-display italic leading-[1.05] text-[12cqw] hyphens-auto line-clamp-5">{title}</span>
          <span className="font-type text-[0.6rem] uppercase tracking-[0.2em] opacity-80">{author}</span>
        </div>
      )}
      <div className="absolute inset-y-0 left-0 w-[5%] bg-gradient-to-r from-black/25 to-transparent" />
    </div>
  );
}

export const Stamp = ({ children, active, onClick, testid }) => {
  const cls = `stamp ${active ? "stamp-active" : ""} ${onClick ? "cursor-pointer hover:-translate-y-0.5" : ""}`;
  return onClick ? (
    <button type="button" className={cls} onClick={onClick} data-testid={testid}>{children}</button>
  ) : (
    <span className={cls} data-testid={testid}>{children}</span>
  );
};

export function CopyButton({ text, testid, label = "Copy" }) {
  const [done, setDone] = useState(false);
  const go = async () => {
    try {
      await copyText(text);
      setDone(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setDone(false), 1500);
    } catch (e) {
      toast.error("Could not copy");
    }
  };
  return (
    <button type="button" className="btn-ghost" onClick={go} data-testid={testid} disabled={!text}>
      {done ? <Check size={13} /> : <Copy size={13} />} {done ? "Copied" : label}
    </button>
  );
}

export const Metric = ({ label, value, testid }) => (
  <div className="border-t border-[#121210] pt-3" data-testid={testid}>
    <p className="label-mono">{label}</p>
    <p className="font-display text-4xl mt-1 tabular-nums">{value}</p>
  </div>
);

export function Footer() {
  return (
    <footer className="border-t border-[#E5DEC9] mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-col md:flex-row gap-4 justify-between">
        <div>
          <p className="font-display text-2xl">AuthorOS</p>
          <p className="font-display italic text-[#5C584E] mt-1" data-testid="footer-slogan">You write. We find the readers.</p>
        </div>
        <div className="flex gap-6 items-start">
          <Link to="/discover" className="label-mono hover:text-[#C84B31]" data-testid="footer-discover-link">Discover</Link>
          <Link to="/signin" className="label-mono hover:text-[#C84B31]" data-testid="footer-signin-link">Sign in</Link>
        </div>
      </div>
    </footer>
  );
}

export const MadeWith = () => (
  <Link to="/" className="inline-flex items-center gap-2 label-mono hover:text-[#C84B31]" data-testid="made-with-authoros">
    <span className="w-2 h-2 bg-[#C84B31] inline-block" /> Made with AuthorOS
  </Link>
);
